/**
 * MEETING FORM MODAL — Modale di registrazione riunione.
 *
 * Aperto da PersonDetail/BusinessDetail (timeline tab 'riunione' o
 * voce "Meeting" nel More menu).
 *
 * Campi (schema yaml `MeetingInput`):
 *  - meetingDate (datetime, required)
 *  - duration (int32 minuti, required) — preset 15/30/45/60/90/120 + custom
 *  - description (string, required)
 *  - status (MeetingStatus enum) — default 'planned'
 *
 * Al submit:
 *  1. dispatch createMeeting(payload) -> Meeting
 *  2. createBidirectionalRelationship Meeting<->record per ogni associato
 *  3. se linkedEntity presente (Detail), anche quello collegato.
 */

import { useState, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { datetimeLocalToIso, nowDatetimeLocal, htmlHasText } from '@crm/shared';
import { RichTextEditor } from '../../components/RichTextEditor';
import { useToast } from '../../components/useToast';
import { useAppDispatch } from '../../store/hooks';
import { createMeeting } from './meetingThunks';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { useAssociatedRecordsBinder } from '../../components/useAssociatedRecordsBinder';
import { AssociatedRecordsField } from '../../components/AssociatedRecordsField';
import {
  MEETING_DURATION_PRESETS,
  MEETING_STATUSES,
  type Meeting,
  type MeetingCreatePayload,
  type MeetingStatus,
} from './meetingTypes';

interface MeetingFormModalProps {
  onClose: () => void;
  onCreated?: () => void;
  linkedEntity?: { className: string; classPK: number };
}

const FORM_ID = 'meeting-form';
const CUSTOM_DURATION_VALUE = -1; // sentinel per "personalizzata" nel Select

function defaultForm(): MeetingCreatePayload {
  return {
    meetingDate: datetimeLocalToIso(nowDatetimeLocal()) ?? '',
    duration: 60,
    description: '',
    status: 'planned',
  };
}

export default function MeetingFormModal({ onClose, onCreated, linkedEntity }: MeetingFormModalProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toast = useToast();

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<MeetingCreatePayload>(() => defaultForm());
  const [meetingDateLocal, setMeetingDateLocal] = useState<string>(() => nowDatetimeLocal());
  // Se l'utente sceglie "Personalizzata" nel Select duration, il form
  // espone un input number che alimenta `form.duration` direttamente.
  const isPresetDuration = MEETING_DURATION_PRESETS.some((p) => p.value === form.duration);
  const [showCustomDuration, setShowCustomDuration] = useState(!isPresetDuration);
  // Rimonta il RichTextEditor (non controllato) per svuotarlo dopo un submit "keep".
  const [editorKey, setEditorKey] = useState(0);

  const meetingFqn = getEntityFqn('Meeting');

  const { records, setRecords, applyAtSubmit, pickerProps } = useAssociatedRecordsBinder({
    mode: 'create',
    entityFqn: meetingFqn ?? undefined,
    reference: linkedEntity ?? null,
  });

  const submitIntentRef = useRef<'close' | 'keep'>('close');

  const canSubmit =
    htmlHasText(form.description) &&
    meetingDateLocal.length > 0 &&
    form.duration > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const intent = submitIntentRef.current;
    submitIntentRef.current = 'close';

    const payload: MeetingCreatePayload = {
      ...form,
      meetingDate: datetimeLocalToIso(meetingDateLocal) ?? '',
    };

    setSubmitting(true);
    try {
      let created: Meeting;
      try {
        created = (await dispatch(createMeeting(payload)).unwrap()) as Meeting;
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        return;
      }
      if (!meetingFqn) {
        console.warn(
          '[relationship] MeetingFormModal: Meeting non registrato nel registry — link saltati',
          { selectedRecords: records.length },
        );
      } else {
        await applyAtSubmit?.(meetingFqn, created.meetingId);
      }
      toast({ severity: 'success', message: t('common.saved') });
      onCreated?.();
      if (intent === 'keep') {
        setForm(defaultForm());
        setMeetingDateLocal(nowDatetimeLocal());
        setEditorKey((k) => k + 1);
        setRecords?.([]);
        setShowCustomDuration(false);
      } else {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  }

  const footer = (
    <>
      <Button
        type="submit"
        form={FORM_ID}
        onClick={() => { submitIntentRef.current = 'close'; }}
        variant="contained"
        disabled={submitting || !canSubmit}
      >
        {t('common.create')}
      </Button>
      <Button type="button" onClick={onClose} variant="outlined">
        {t('common.cancel')}
      </Button>
    </>
  );

  // Valore mostrato nel Select duration: il valore preset se matcha, altrimenti
  // il sentinel CUSTOM_DURATION_VALUE per indicare "Personalizzata".
  const selectDurationValue = isPresetDuration ? form.duration : CUSTOM_DURATION_VALUE;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">{t('meeting.createTitle')}</span>
        <IconButton
          aria-label={t('common.cancel')}
          onClick={onClose}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <form id={FORM_ID} onSubmit={handleSubmit} className="meeting-form">
          {/* Grid 2-col: Data/ora | Durata */}
          <div className="task-form__grid task-form__grid--2col">
            <TextField
              type="datetime-local"
              label={t('meeting.meetingDate')}
              value={meetingDateLocal}
              onChange={(e) => setMeetingDateLocal(e.target.value)}
              required
              fullWidth
              className="form-field"
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              select
              label={t('meeting.duration')}
              value={selectDurationValue}
              onChange={(e) => {
                const val = Number(e.target.value);
                if (val === CUSTOM_DURATION_VALUE) {
                  setShowCustomDuration(true);
                  // Non tocco form.duration: l'utente compila il custom input sotto.
                } else {
                  setShowCustomDuration(false);
                  setForm({ ...form, duration: val });
                }
              }}
              required
              fullWidth
              className="form-field"
            >
              {MEETING_DURATION_PRESETS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{t(p.label)}</MenuItem>
              ))}
              <MenuItem value={CUSTOM_DURATION_VALUE}>{t('meeting.durationCustom')}</MenuItem>
            </TextField>
          </div>

          {/* Custom duration input (visibile solo se "Personalizzata" selezionata
              o se il valore corrente non e' un preset). */}
          {showCustomDuration && (
            <TextField
              type="number"
              label={`${t('meeting.duration')} (${t('meeting.durationMinutes')})`}
              value={form.duration > 0 ? form.duration : ''}
              onChange={(e) => setForm({ ...form, duration: Number(e.target.value) || 0 })}
              slotProps={{ htmlInput: { min: 1 } }}
              required
              fullWidth
              className="form-field"
            />
          )}

          {/* Status */}
          <TextField
            select
            label={t('meeting.status')}
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value as MeetingStatus })}
            required
            fullWidth
            className="form-field"
          >
            {MEETING_STATUSES.map((s) => (
              <MenuItem key={s.value} value={s.value}>{t(s.label)}</MenuItem>
            ))}
          </TextField>

          {/* Descrizione WYSIWYG */}
          <div className="form-field">
            <label className="field-row__label text-xs text-muted">{t('meeting.description')}</label>
            <RichTextEditor
              key={editorKey}
              onChange={(html) => setForm({ ...form, description: html })}
              placeholder={t('meeting.descriptionPlaceholder')}
            />
          </div>

          {/* Associa ai record (polimorfico) */}
          <AssociatedRecordsField
            {...pickerProps}
            records={records}
            onChange={(val) => setRecords?.(val)}
            displayMode="click-to-edit"
          />
        </form>
      </DialogContent>
      <DialogActions className="app-dialog__footer">{footer}</DialogActions>
    </Dialog>
  );
}
